import {
  CopyOutlined,
  EllipsisOutlined,
  FormOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import {
  Table,
  TableColumnsType,
  TableProps,
  Tooltip,
  Badge,
  Button,
  Dropdown,
  Menu,
  message,
  Switch,
  Tag,
  Modal,
} from 'antd';
import EditorPreviewer from 'components/EditorPreviewer';
import { mustache2SlateValuePolicyMsgTypes } from 'components/TemplateEditor/mustacheUtils';
import dayjs from 'lib/dayjs';
import { isValidContent } from 'services/api/oms';
import { regionTimeZoneMap } from 'services/config/region';
import { Descendant } from 'slate';
import toLowerCase from 'utils/toLowerCase';
import StatusSwitch from 'components/StatusSwitch';
import { noop, toNumber } from 'lodash-es';
import {
  cancellingTradeScenarios,
  isNoRegionScenario,
  isReturnTradeScenarioType,
  isReviewManagementScenarioType,
} from 'views/Aide/Scenario/config';
import { useShippingStatusLabel } from 'views/Aide/Scenario/PolicyForm/share';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { useGetTimes } from 'views/Aide/Scenario/RoundForm';
import {
  UpdatePolicyPriorityBody,
  deletePolicy,
  updatePolicyEnable,
  updatePolicyPriority,
} from '../service';
import PopoverPreview from 'components/StoreScopes/PopoverPreview';
import { ALL2all } from './TaskForm/StoreScopesField';
import { stores2storeScopes, useExternalStoreMap } from './NewPolicy/StoresField';
import { useTranslate, TFnType } from '@tolgee/react';
import { PolicyName } from './NewPolicy/useGetPolicyName';
import { tolgee } from 'services/i18n/tolgee';

export const What = styled(QuestionCircleOutlined)`
  margin-left: 8px;
  font-weight: 400;
  color: var(--theme-text-3);
  cursor: help;
`;

export const ButtonWrapper = styled.div`
  button {
    padding-left: 0;
    padding-right: 24px;
  }
`;

export const Template = styled.div`
  cursor: pointer;
  > div {
    width: 100%;
    height: 18px;
    > * {
      display: flex;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    * {
      display: inline-block;
      vertical-align: top;
      height: 18px;
      max-width: 100%;
      margin-top: 0;
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

export const styles = {
  tooltip: css`
    max-width: 800px;
  `,
  preLine: css`
    white-space: pre-line;
  `,
};

type TR = Aide.Policy;

export type Chunk = {
  type:
    | 'text'
    | CustomSlateNodeType.IMAGE
    | CustomSlateNodeType.VIDEO
    | CustomSlateNodeType.STICKER
    | CustomSlateNodeType.FOLLOW
    | CustomSlateNodeType.QRCODE
    | CustomSlateNodeType.ITEM
    | CustomSlateNodeType.LOGISTICS
    | CustomSlateNodeType.VOUCHER;
  content: Descendant[];
  translatedContent?: Descendant[];
};

export const getChunks = (context: { content: string; translatedContent: string }) => {
  const { content, translatedContent } = context;

  let originSegments: Descendant[] = [];
  let translatedSegments: Descendant[] = [];

  try {
    originSegments = mustache2SlateValuePolicyMsgTypes(content.replace(/^#mustache#/, ''));
    translatedSegments = mustache2SlateValuePolicyMsgTypes(
      translatedContent.replace(/^#mustache#/, ''),
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }

  const chunks = originSegments.reduce<Chunk[]>((res, segment, index) => {
    const lastChunk = res[res.length - 1];

    const translatedSegment = translatedSegments[index];

    if (segment.type === CustomSlateNodeType.ITEM) {
      res.push({
        type: CustomSlateNodeType.ITEM,
        content: [],
      });

      return res;
    }

    if (segment.type === CustomSlateNodeType.VOUCHER) {
      res.push({
        type: CustomSlateNodeType.VOUCHER,
        content: [],
      });

      return res;
    }

    if (segment.type === CustomSlateNodeType.LOGISTICS) {
      res.push({
        type: CustomSlateNodeType.LOGISTICS,
        content: [],
      });

      return res;
    }

    if (segment.type !== CustomSlateNodeType.IMAGE) {
      if (lastChunk && lastChunk.type === 'text') {
        lastChunk.content.push(segment);

        if (translatedSegment && translatedSegment.type === segment.type) {
          if (lastChunk.translatedContent) {
            lastChunk.translatedContent.push(translatedSegment);
          } else {
            lastChunk.translatedContent = [translatedSegment];
          }
        }
      } else {
        res.push({
          type: 'text',
          content: [segment],
          translatedContent:
            translatedSegment && translatedSegment.type === segment.type
              ? [translatedSegment]
              : undefined,
        });
      }

      return res;
    }

    const { children: [{ text = 'image' } = {}] = [] } = segment;

    const textTypeMap = {
      image: CustomSlateNodeType.IMAGE,
      video: CustomSlateNodeType.VIDEO,
      sticker: CustomSlateNodeType.STICKER,
      follow: CustomSlateNodeType.FOLLOW,
      qrcode: CustomSlateNodeType.QRCODE,
      item: CustomSlateNodeType.ITEM,
      voucher: CustomSlateNodeType.VOUCHER,
    } as const;

    const type = textTypeMap[text as keyof typeof textTypeMap] || CustomSlateNodeType.IMAGE;

    if (lastChunk && lastChunk.type === type) {
      lastChunk.content.push(segment);
    } else {
      res.push({
        type,
        content: [segment],
      });
    }

    return res;
  }, []);

  return chunks;
};

export const statusRender = (
  record: Pick<TR, 'enable' | 'termOfValidity'>,
  timeZone: string = 'Asia/Shanghai',
) => {
  const {
    enable,
    termOfValidity: { type, dateTimeQuantum, timeQuantumGroup, multiTimeQuantum },
  } = record;

  if (!enable) {
    return <Badge status="warning" text={tolgee.t('marketing.未启用')} />;
  }

  if (type === 'LONG_TERM') {
    return <Badge status="success" text={tolgee.t('marketing.生效中')} />;
  }

  if (type === 'DATE_TIME_QUANTUM' && dateTimeQuantum) {
    const { startDateTime, endDateTime } = dateTimeQuantum;

    if (startDateTime && endDateTime) {
      const now = dayjs();

      if (now.isBefore(dayjs.tz(startDateTime, timeZone))) {
        return <Badge color="geekblue" text={tolgee.t('marketing.未开始')} />;
      }

      if (now.isAfter(dayjs.tz(endDateTime, timeZone))) {
        return <Badge status="default" text={tolgee.t('marketing.已过期')} />;
      }
    }
  }

  if (type === 'TIME_QUANTUM_GROUP' && timeQuantumGroup) {
    const { startDate, endDate, startTime, endTime } = timeQuantumGroup;

    const startDateTime = `${startDate} ${startTime}`;
    const endDateTime = `${endDate} ${endTime}`;

    if (startDateTime && endDateTime) {
      const now = dayjs();

      if (now.isBefore(dayjs.tz(startDateTime, timeZone))) {
        return <Badge color="geekblue" text={tolgee.t('marketing.未开始')} />;
      }
      if (now.isAfter(dayjs.tz(endDateTime, timeZone))) {
        return <Badge status="default" text={tolgee.t('marketing.已过期')} />;
      }
    }
  }

  if (type === 'MULTI_TIME_QUANTUM' && multiTimeQuantum) {
    const { startDate, endDate, timeQuantum } = multiTimeQuantum;
    const startTime = `${startDate.slice(0, -6)} ${timeQuantum[0].startTime}:00`;
    const endTime = `${endDate.slice(0, -6)} ${timeQuantum[timeQuantum.length - 1].endTime}:59`;
    const now = dayjs();

    if (now.isBefore(dayjs.tz(startTime, timeZone))) {
      return <Badge color="geekblue" text={tolgee.t('marketing.未开始')} />;
    }
    if (now.isAfter(dayjs.tz(endTime, timeZone))) {
      return <Badge status="default" text={tolgee.t('marketing.已过期')} />;
    }
  }

  return <Badge status="success" text={tolgee.t('marketing.生效中')} />;
};

export const termOfValidityRender = (termOfValidity: Marketing.TermOfValidity) => {
  const { type, dateTimeQuantum, timeQuantumGroup, multiTimeQuantum } = termOfValidity;

  if (type === 'LONG_TERM') {
    return tolgee.t('marketing.长期');
  }

  if (type === 'DATE_TIME_QUANTUM' && dateTimeQuantum) {
    const { startDateTime = '', endDateTime = '' } = dateTimeQuantum;

    return `${startDateTime} ~ ${endDateTime}`;
  }

  if (type === 'TIME_QUANTUM_GROUP' && timeQuantumGroup) {
    const { startDate = '', endDate = '', startTime = '', endTime = '' } = timeQuantumGroup;

    return `${startDate} ~ ${endDate}\n${tolgee.t('marketing.每天')} ${startTime}-${endTime}`;
  }

  if (type === 'MULTI_TIME_QUANTUM' && multiTimeQuantum) {
    const { startDate, endDate, timeQuantum } = multiTimeQuantum;

    return `${startDate.slice(0, -6)} ~ ${endDate.slice(0, -6)}\n${tolgee.t(
      'marketing.每天',
    )} ${timeQuantum.map(({ startTime, endTime }) => `${startTime}-${endTime}`).join(' ')}`;
  }

  return '-';
};

export const chunksRender = (chunks: Chunk[], t: TFnType) => {
  if (chunks.length === 0) {
    return '-';
  }

  const isTextOnly = chunks.length === 1 && chunks[0].type === 'text';

  if (!isTextOnly) {
    const chunkTypeLabelMap = {
      text: t('phrase.文本'),
      image: t('phrase.图片'),
      video: t('phrase.视频'),
      sticker: t('messageType.STICKER'),
      follow: t('phrase.邀请关注'),
      qrcode: '自动发货二维码',
      item: t('messageType.商品卡片'),
      voucher: t('messageType.VOUCHER'),
      logistics: t('物流卡片'),
    };

    return chunks.map(({ type }) => `[${chunkTypeLabelMap[type]}]`).join('');
  }

  const [{ content: value, translatedContent: translatedValue = [] }] = chunks;

  return (
    <Template
      className={css`
        span {
          line-height: 18px;
        }
      `}
    >
      <EditorPreviewer value={isValidContent(value) ? value : translatedValue} hoverPop />
    </Template>
  );
};

export interface PolicyTableProps extends TableProps<TR> {
  scenarioType: Marketing.AllScenarioType;
  region: General.UpperCaseRegion | 'ALL';
  platform: General.PlatformUpperCase;
  maxRound: number;
  searchValue: Omit<UpdatePolicyPriorityBody, 'policyId' | 'action'>;
  onPolicyEdit: (record: TR) => void;
  onPolicyCopy: (record: TR, round: number) => void;
  onUpdated?: () => void;
}

function PolicyTable(props: PolicyTableProps) {
  const {
    scenarioType,
    region: upperCaseRegion,
    platform,
    maxRound,
    searchValue,
    pagination,
    onPolicyEdit,
    onPolicyCopy,
    onUpdated = noop,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const region = toLowerCase(upperCaseRegion);

  const { getTimes } = useGetTimes();

  const timeZone =
    platform === 'ALIEXPRESS'
      ? regionTimeZoneMap.gb
      : region !== 'all'
      ? regionTimeZoneMap[region]
      : Intl.DateTimeFormat().resolvedOptions().timeZone;

  const shippingStatusLabelMap = useShippingStatusLabel();

  const isCancellingScenario = cancellingTradeScenarios.includes(scenarioType);

  const isReviewManagement = isReviewManagementScenarioType(scenarioType);

  const isNoRegion = isNoRegionScenario(scenarioType);

  const { current, pageSize, total } = pagination || {};

  const externalStoreMap = useExternalStoreMap(
    platform,
    isNoRegion || upperCaseRegion === 'ALL' ? undefined : upperCaseRegion,
  );

  const columns: TableColumnsType<TR> = [
    {
      key: 'priority',
      dataIndex: 'priority',
      width: 130,
      title: (
        <div>
          {t('aide.执行顺序')}
          <Tooltip
            overlayClassName={styles.tooltip}
            placement="topLeft"
            arrowPointAtCenter
            title={t('aide.同一订单满足店铺下多个策略时，仅执行优先级最靠前的策略')}
          >
            <What />
          </Tooltip>
        </div>
      ),
      render: (_, __, index) =>
        current && pageSize ? pageSize * (current - 1) + index + 1 : index + 1,
    },
    {
      key: 'name',
      dataIndex: 'name',
      width: 200,
      title: t('marketing.策略名称'),
      render: (name: string) => <PolicyName name={name} />,
    },
    ...(scenarioType === 'RECEPTION_SCORE'
      ? []
      : [
          {
            key: 'template',
            title: t('reviewSetting.发送内容'),
            width: 400,
            render: (_: any, record: TR) => {
              const {
                bilingualExecuteContexts = [],
                cancelTradeConfig: { autoCancel = false, contents = [] } = {},
                regionExecuteContexts = [],
              } = record;

              const { content = '', translatedContent = '' } =
                (() => {
                  if (isNoRegion) {
                    return regionExecuteContexts.find(({ enable = true }) => enable)
                      ?.executeContext;
                  }

                  if (isCancellingScenario || scenarioType === 'CANCELLING_DELIVERY_FAIL') {
                    return (
                      !autoCancel
                        ? contents[0]
                        : contents.find(({ sendPoint }) => sendPoint === 'CANCELLED')
                    )?.executeContext;
                  }

                  return bilingualExecuteContexts[0];
                })() || {};

              const chunks = getChunks({ content, translatedContent });

              return chunksRender(chunks, t);
            },
          },
        ]),
    ...(isReviewManagement || scenarioType === 'DELAY_SHIP'
      ? []
      : [
          {
            key: 'status',
            width: 120,
            title: (
              <div data-guide="marketing-guide-step-4">
                {t('aide.策略状态')}
                <Tooltip
                  overlayClassName={styles.tooltip}
                  title={
                    <div>
                      {`${t('marketing.生效中')}: ${t(
                        'aide.策略开关为开，且当前时间在策略有效期内',
                      )}`}
                      <br />
                      {`${t('marketing.未开始')}: ${t(
                        'aide.策略开关为开，且当前时间在策略生效开始时间之前',
                      )}`}
                      <br />
                      {`${t('marketing.已过期')}: ${t(
                        'aide.策略开关为开，且当前时间在策略生效结束时间之后',
                      )}`}
                      <br />
                      {`${t('marketing.未启用')}: ${t('aide.策略开关为关')}`}
                    </div>
                  }
                >
                  <What />
                </Tooltip>
              </div>
            ),
            render: (_: any, record: TR) => {
              return statusRender(record, timeZone);
            },
          },
        ]),
    ...(!isReviewManagement
      ? [
          {
            key: 'termOfValidity',
            dataIndex: 'termOfValidity',
            title:
              scenarioType === 'DELAY_SHIP' ? t('aide.限定买家下单时间') : t('marketing.有效期'),
            width: 250,
            className: styles.preLine,
            render: (termOfValidity: TR['termOfValidity']) => {
              if (scenarioType === 'DELAY_SHIP' && termOfValidity.type === 'LONG_TERM') {
                return t('aide.不限');
              }

              return termOfValidityRender(termOfValidity);
            },
          },
        ]
      : [
          {
            key: 'stars',
            dataIndex: 'autoReviewConfig',
            width: 200,
            title: t('reviewSetting.适用评分'),
            render: (config: TR['autoReviewConfig']) => {
              const { stars = [] } = config || {};

              return stars.length
                ? stars.map((star) => t('review.x星', { count: toNumber(star) })).join(' ')
                : '-';
            },
          },
        ]),
    ...(scenarioType === 'NEGATIVE_REVIEW_REPLY' || scenarioType === 'POSITIVE_REVIEW_REPLY'
      ? [
          {
            key: 'delayDay',
            dataIndex: 'autoReviewConfig',
            width: 150,
            title: (
              <div>
                {t('aide.延时回评')}
                <Tooltip
                  title={t(
                    'aide.同一个店铺下，同时配置了同一星级的中差评自动回评与中差评挽回，支持延时触发中差评自动回评。',
                  )}
                >
                  <What />
                </Tooltip>
              </div>
            ),
            render: (config: TR['autoReviewConfig']) => {
              const { delayDay, delayAutoReview } = config || {};

              return !delayAutoReview || delayDay === 0 ? '-' : t('aide.x天', { x: delayDay });
            },
          },
        ]
      : []),
    ...(isCancellingScenario && platform === 'SHOPEE'
      ? [
          {
            key: 'shippingStatus',
            dataIndex: 'cancelTradeConfig',
            width: 150,
            title: t('aide.出货进度'),
            render: (cancelTradeConfig: TR['cancelTradeConfig']) => {
              const { shippingStatus = [] } = cancelTradeConfig || {};

              return shippingStatus.map((status) => {
                return <div>{shippingStatusLabelMap[status]}</div>;
              });
            },
          },
          {
            key: 'autoCancel',
            dataIndex: 'cancelTradeConfig',
            width: 150,
            title: t('aide.自动同意买家申请'),
            render: (cancelTradeConfig: TR['cancelTradeConfig']) => {
              const { autoCancel = false } = cancelTradeConfig || {};

              return (
                <Switch
                  disabled
                  checked={autoCancel}
                  checkedChildren={t('aide.开')}
                  unCheckedChildren={t('aide.关')}
                />
              );
            },
          },
        ]
      : []),
    {
      key: 'storeScopes',
      width: 150,
      title: t('marketing.适用店铺'),
      render: (_, { regionStoreList, stores }) => {
        if (isNoRegion || upperCaseRegion === 'ALL') {
          const scopes = regionStoreList?.length
            ? [
                {
                  platform,
                  regionStoreScopes: ALL2all(regionStoreList),
                  storeCount: 0,
                },
              ]
            : [];

          return <PopoverPreview value={scopes} />;
        }

        return (
          <PopoverPreview
            value={stores2storeScopes(stores, platform, upperCaseRegion, externalStoreMap)}
          />
        );
      },
    },
    {
      key: 'enable',
      dataIndex: 'enable',
      width: 120,
      title: t('aide.策略开关'),
      render: (enable: TR['enable'], record) => {
        return (
          <StatusSwitch
            checked={enable}
            onChange={(enable) => {
              return updatePolicyEnable({
                policyId: record.id,
                enable,
              })
                .then(() => {
                  message.success(t('operation.saveSucceed'));

                  onUpdated();
                })
                .catch(() => {
                  message.error(t('operation.saveFailed'));
                });
            }}
            checkedChildren={t('aide.开')}
            unCheckedChildren={t('aide.关')}
          />
        );
      },
    },
    ...(isReturnTradeScenarioType(scenarioType)
      ? [
          {
            key: 'returnType',
            width: 170,
            title: t('aide.申请售后类型'),
            render: (_: any, record: TR) => {
              const { returnTradeConfig: { returnType = [] } = {} } = record;

              if (returnType.length === 0) {
                return '-';
              }

              return (
                <div>
                  {returnType.map((returnType) => {
                    return (
                      <Tag key={returnType} color="blue">
                        {
                          {
                            ONLY_REFUND: t('aide.仅退款'),
                            RETURN_REFUND: t('aide.退货退款'),
                          }[returnType]
                        }
                      </Tag>
                    );
                  })}
                </div>
              );
            },
          },
        ]
      : []),
    {
      key: 'operation',
      title: t('操作'),
      width: 300,
      fixed: 'right',
      render: (_, record: TR, index) => {
        const { id: policyId, knowledgeId } = record;

        const isPriorityFirst = index === 0 && current === 1;
        const isPriorityLast =
          current && pageSize && total ? index + (current - 1) * pageSize + 1 === total : false;

        return (
          <ButtonWrapper>
            <Button
              key="edit"
              type="link"
              icon={<FormOutlined />}
              onClick={() => onPolicyEdit(record)}
            >
              {t('marketing.编辑')}
            </Button>
            {maxRound > 1 && platform !== 'ALIEXPRESS' ? (
              <Dropdown
                key="copy"
                overlay={
                  <Menu onClick={(e) => onPolicyCopy(record, e.key as any)}>
                    {new Array(maxRound).fill('').map((_, index) => {
                      return (
                        <Menu.Item key={index + 1}>
                          {t('marketing.复制到第x次', { x: getTimes(index + 1) })}
                        </Menu.Item>
                      );
                    })}
                  </Menu>
                }
              >
                <Button type="link" icon={<CopyOutlined />}>
                  {t('marketing.复制')}
                </Button>
              </Dropdown>
            ) : (
              <Button type="link" icon={<CopyOutlined />} onClick={() => onPolicyCopy(record, 1)}>
                {t('marketing.复制')}
              </Button>
            )}
            <Dropdown
              key="more"
              overlay={
                <Menu
                  onClick={({ key }) => {
                    if (key === 'delete') {
                      Modal.confirm({
                        title: t('marketing.您确定删除该策略吗？'),
                        onOk: () => {
                          deletePolicy(policyId)
                            .then(onUpdated)
                            .catch(() => {
                              message.error(t('marketing.删除策略失败，请稍后再试'));
                            });
                        },
                      });
                      return;
                    }

                    updatePolicyPriority({
                      ...searchValue,
                      policyId,
                      action: key as any,
                    })
                      .then(onUpdated)
                      .catch(() => {
                        message.error(t('operation.saveFailed'));
                      });
                  }}
                >
                  {total !== undefined && total > 1 && (
                    <>
                      {!isPriorityFirst && (
                        <>
                          <Menu.Item key="TOP">{t('marketing.置顶')}</Menu.Item>
                          <Menu.Item key="UP">{t('marketing.上移')}</Menu.Item>
                        </>
                      )}
                      {!isPriorityLast && (
                        <>
                          <Menu.Item key="DOWN">{t('marketing.下移')}</Menu.Item>
                          <Menu.Item key="BOTTOM">{t('marketing.置底')}</Menu.Item>
                        </>
                      )}
                    </>
                  )}
                  {knowledgeId ? null : <Menu.Item key="delete">{t('marketing.删除')}</Menu.Item>}
                </Menu>
              }
            >
              <Button type="link" icon={<EllipsisOutlined />}>
                {t('marketing.更多')}
              </Button>
            </Dropdown>
          </ButtonWrapper>
        );
      },
    },
  ];

  return (
    <Table
      rowKey="id"
      scroll={{ x: '100%' }}
      pagination={pagination}
      columns={columns}
      {...otherProps}
    />
  );
}

export default PolicyTable;
